// ════════════════════════════════════════════════════════════════════════════
// M2.7 — descriptores PEMM-Proceso (Hammer 2007, "The Process Audit", HBR)
// Madurez de UN proceso: 13 habilitadores en 5 categorías × 4 niveles (P-1…P-4).
// Contenido metodológico (ES) fiel a la matriz PEMM de Hammer; lo consume el
// heatmap por proceso (tooltips + leyenda). Espejo de descriptores-madurez-proceso.ts.
// ⚠️ Cross-check pendiente contra el manual del máster (Tabla 5) / caso ZENIC antes
// de claims de marketing — ver ADR-038 adenda.
// ════════════════════════════════════════════════════════════════════════════

export type PNivel = 'P1' | 'P2' | 'P3' | 'P4';
export type CategoriaPEMM =
  | 'DISENO'
  | 'EJECUTANTES'
  | 'PROPIETARIO'
  | 'INFRAESTRUCTURA'
  | 'METRICAS';

export interface NivelInfoPemm {
  nivel: PNivel;
  etiqueta: string;
  nombre: string;
  resumen: string;
  color: 'VERDE' | 'AMARILLO' | 'ROJO';
}

/** Los 4 niveles PEMM de Hammer. Global del proceso = eslabón débil (MIN de habilitadores). */
export const NIVELES_PEMM: NivelInfoPemm[] = [
  {
    nivel: 'P1',
    etiqueta: 'P-1',
    nombre: 'Funcional',
    resumen:
      'El habilitador opera a nivel de función/silo; el proceso aún no es extremo a extremo.',
    color: 'ROJO',
  },
  {
    nivel: 'P2',
    etiqueta: 'P-2',
    nombre: 'Extremo a extremo',
    resumen:
      'El proceso está definido y gestionado de principio a fin, según las necesidades del cliente.',
    color: 'AMARILLO',
  },
  {
    nivel: 'P3',
    etiqueta: 'P-3',
    nombre: 'Integrado en la empresa',
    resumen:
      'El proceso encaja con otros procesos y con la estrategia para optimizar el rendimiento corporativo.',
    color: 'VERDE',
  },
  {
    nivel: 'P4',
    etiqueta: 'P-4',
    nombre: 'Inter-empresa',
    resumen: 'El proceso se optimiza extendiéndose a clientes y proveedores (sistema de valor).',
    color: 'VERDE',
  },
];

export interface CategoriaInfoPemm {
  categoria: CategoriaPEMM;
  nombre: string;
}
/** Las 5 categorías de habilitadores PEMM (3+3+3+2+2 = 13). */
export const CATEGORIAS_PEMM: CategoriaInfoPemm[] = [
  { categoria: 'DISENO', nombre: 'Diseño' },
  { categoria: 'EJECUTANTES', nombre: 'Ejecutantes' },
  { categoria: 'PROPIETARIO', nombre: 'Propietario' },
  { categoria: 'INFRAESTRUCTURA', nombre: 'Infraestructura' },
  { categoria: 'METRICAS', nombre: 'Métricas' },
];

export interface HabilitadorPemm {
  categoria: CategoriaPEMM;
  clave: string;
  nombre: string;
}
/** Los 13 habilitadores PEMM en orden canónico (Hammer 2007). */
export const HABILITADORES_PEMM: HabilitadorPemm[] = [
  { categoria: 'DISENO', clave: 'proposito', nombre: 'Propósito' },
  { categoria: 'DISENO', clave: 'contexto', nombre: 'Contexto' },
  { categoria: 'DISENO', clave: 'documentacion', nombre: 'Documentación' },
  { categoria: 'EJECUTANTES', clave: 'conocimiento', nombre: 'Conocimiento' },
  { categoria: 'EJECUTANTES', clave: 'habilidades', nombre: 'Habilidades' },
  { categoria: 'EJECUTANTES', clave: 'comportamiento', nombre: 'Comportamiento' },
  { categoria: 'PROPIETARIO', clave: 'identidad', nombre: 'Identidad' },
  { categoria: 'PROPIETARIO', clave: 'actividades', nombre: 'Actividades' },
  { categoria: 'PROPIETARIO', clave: 'autoridad', nombre: 'Autoridad' },
  { categoria: 'INFRAESTRUCTURA', clave: 'sistemas_info', nombre: 'Sistemas de información' },
  { categoria: 'INFRAESTRUCTURA', clave: 'sistemas_rrhh', nombre: 'Sistemas de RR.HH.' },
  { categoria: 'METRICAS', clave: 'definicion', nombre: 'Definición' },
  { categoria: 'METRICAS', clave: 'usos', nombre: 'Usos' },
];

/** Nota de método del modelo PEMM-Proceso. */
export const METODO_PEMM =
  'PEMM (Hammer 2007): el proceso alcanza el nivel P-n solo si TODOS sus habilitadores llegan a P-n (eslabón débil/transitividad). Nivel del proceso = mínimo de los 13 habilitadores + semáforo.';

/** Matriz 13×4: qué significa cada nivel P en cada habilitador (Hammer). */
export const DESCRIPTORES_HABILITADOR: Record<string, Record<PNivel, string>> = {
  proposito: {
    P1: 'El proceso se ha diseñado para optimizar el rendimiento de la función a la que pertenece.',
    P2: 'El proceso se ha diseñado de extremo a extremo para optimizar su rendimiento global.',
    P3: 'El diseño encaja con otros procesos y sistemas para optimizar el rendimiento de la empresa.',
    P4: 'El diseño encaja con los procesos de clientes y proveedores (inter-empresa).',
  },
  contexto: {
    P1: 'Están identificados entradas, salidas, proveedores y clientes del proceso.',
    P2: 'Las necesidades de los clientes del proceso son conocidas y acordadas.',
    P3: 'El propietario y los de otros procesos acuerdan métricas e interfaces.',
    P4: 'Las expectativas se fijan con clientes y proveedores dentro del sistema de valor.',
  },
  documentacion: {
    P1: 'La documentación es principalmente funcional, si existe.',
    P2: 'Hay documentación de extremo a extremo del diseño del proceso.',
    P3: 'Describe interfaces con otros procesos y enlaza con la arquitectura de datos y sistemas.',
    P4: 'Documentación electrónica y dinámica; soporta la ejecución y la gestión del proceso.',
  },
  conocimiento: {
    P1: 'Los ejecutantes saben nombrar el proceso y las métricas con las que se mide.',
    P2: 'Describen el flujo global y cómo su trabajo afecta a clientes y a otros ejecutantes.',
    P3: 'Conocen conceptos de negocio y cómo su trabajo afecta al rendimiento de la empresa.',
    P4: 'Conocen tendencias del sector y cómo su trabajo afecta al rendimiento inter-empresa.',
  },
  habilidades: {
    P1: 'Resolución de problemas y mejora del proceso.',
    P2: 'Trabajo en equipo y autogestión.',
    P3: 'Toma de decisiones de negocio.',
    P4: 'Gestión e implementación del cambio.',
  },
  comportamiento: {
    P1: 'Cierta lealtad al proceso, pero sobre todo a la función.',
    P2: 'Siguen el diseño, lo ejecutan correctamente y ayudan a los demás a ejecutarlo.',
    P3: 'Se esfuerzan por que el proceso entregue resultados para los objetivos de la empresa.',
    P4: 'Buscan señales de que el proceso debe cambiar y proponen mejoras.',
  },
  identidad: {
    P1: 'El propietario es una persona o grupo informal encargado de mejorar el proceso.',
    P2: 'Existe un rol oficial de propietario, ocupado por un directivo con peso y credibilidad.',
    P3: 'El propietario forma parte del órgano de decisión más alto de la empresa.',
    P4: 'El propietario está en el máximo órgano de gobierno y puede liderar un comité de procesos.',
  },
  actividades: {
    P1: 'Identifica y documenta el proceso, lo comunica y patrocina cambios pequeños.',
    P2: 'Comunica objetivos, patrocina el rediseño y la mejora, planifica la implantación.',
    P3: 'Trabaja con otros propietarios para integrar procesos hacia los objetivos de empresa.',
    P4: 'Desarrolla el plan estratégico del proceso y colabora con clientes y proveedores.',
  },
  autoridad: {
    P1: 'Defiende el proceso pero solo puede persuadir a los responsables funcionales.',
    P2: 'Controla el diseño del proceso e influye en su presupuesto y en el personal.',
    P3: 'Controla el presupuesto de TI y los proyectos que cambian el proceso; influye en asignación y evaluación.',
    P4: 'Controla el presupuesto del proceso y tiene fuerte influencia sobre el personal.',
  },
  sistemas_info: {
    P1: 'Sistemas heredados y fragmentados dan soporte al proceso.',
    P2: 'Un sistema construido a partir de componentes funcionales soporta el proceso.',
    P3: 'Un sistema integrado, diseñado para el proceso y conforme a estándares de empresa, lo soporta.',
    P4: 'Un sistema de arquitectura modular y estándares del sector soporta la comunicación inter-empresa.',
  },
  sistemas_rrhh: {
    P1: 'Los directivos funcionales premian la excelencia y la resolución de problemas funcionales.',
    P2: 'El diseño del proceso define roles, descripciones de puesto, perfiles de competencia y formación.',
    P3: 'Contratación, desarrollo y recompensas enfatizan necesidades y resultados del proceso.',
    P4: 'Los sistemas de RR.HH. refuerzan la colaboración intra e inter-empresa.',
  },
  definicion: {
    P1: 'El proceso tiene métricas básicas de coste y calidad (funcionales).',
    P2: 'Tiene métricas de extremo a extremo derivadas de los requisitos del cliente.',
    P3: 'Las métricas derivan de los objetivos estratégicos de la empresa.',
    P4: 'Las métricas derivan de objetivos inter-empresa.',
  },
  usos: {
    P1: 'Se usan para seguir el rendimiento y resolver las causas de fallos funcionales.',
    P2: 'Se usan para comparar con benchmarks y necesidades del cliente, y fijar metas.',
    P3: 'Se presentan a los ejecutantes para concienciar y motivar; se usan en la gestión diaria.',
    P4: 'Se revisan y refrescan periódicamente y se usan en la planificación estratégica.',
  },
};

/** Descriptor de una celda (habilitador × nivel). */
export function descriptorHabilitador(clave: string, nivel: PNivel): string {
  return DESCRIPTORES_HABILITADOR[clave]?.[nivel] ?? '';
}
